import { Meal } from "@/clients/models/Meal";
import { PageContainer } from "@/components/container/page-container";
import { SafeAreaContent } from "@/components/content/safe-area-content";
import { useEditMealNutrition } from "@/hooks/requests/meal/use-edit-meal-nutrition";
import { useGetOtherPreferences } from "@/hooks/requests/other-preferences/use-get-other-preferences";
import { useToast } from "@/hooks/ui/use-toast";
import { aiChatModalOpenAtom } from "@/stores/chat.store";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonPage, useIonRouter, useIonViewWillLeave } from "@ionic/react";
import { useSetAtom } from "jotai";
import { useCallback, useRef, useState } from "react";
import { MealAllergensTags } from "../../components/meal-allergens-tags";
import { MealDescription } from "../../components/meal-description";
import { MealDetailActions } from "../../components/meal-detail-actions";
import { MealDetailAddMealFab } from "../../components/meal-detail-add-meal-fab";
import { MealDetailHeader } from "../../components/meal-detail-header";
import { MealDetailMacros } from "../../components/meal-detail-macros";
import {
  AmendMacrosModal,
  NutritionValues,
} from "../../components/meal-detail-macros/amend-macros-modal";
import { MealDetailMeta } from "../../components/meal-detail-meta";
import { MealDetailTags } from "../../components/meal-detail-tags";
import { MealFeedbackSection } from "../../components/meal-feedback-section";
import { MealIngredientsSection } from "../../components/meal-ingredients-section";
import { PlanOverviewImage } from "../../components/plan-overview-image";
import { useMealDetailData } from "../../hooks/use-meal-detail-data";

export default function MealDetailPage() {
  const { otherPreferences } = useGetOtherPreferences();
  const showCaloriesAndMacros = otherPreferences?.showCaloriesAndMacros ?? false;
  const { getMealDetailData } = useMealDetailData();
  const mealPlan = getMealDetailData();
  const mealData: Meal | null = mealPlan?.meal ?? null;
  const mealTime = mealPlan?.mealTime;
  const mealDay = mealPlan?.mealDay;
  const targetDate = mealPlan?.targetDate;
  const fromChatPicker = mealPlan?.fromChatPicker ?? false;
  const fromPlan = mealPlan?.fromPlan ?? false;
  const pageRef = useRef<HTMLElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ingredientsSectionRef = useRef<HTMLDivElement>(null);
  const [isNutritionEditOpen, setIsNutritionEditOpen] = useState(false);
  const navigation = useIonRouter();
  const setAiChatModalOpen = useSetAtom(aiChatModalOpenAtom);
  const { editMealNutritionAsync, isEditingMealNutrition } = useEditMealNutrition();
  const { showError, showSuccess } = useToast();

  const scrollToIngredients = useCallback(() => {
    if (ingredientsSectionRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: ingredientsSectionRef.current.offsetTop - 100,
        behavior: "smooth",
      });
    }
  }, []);

  // Opened from a top-pick card in the AI chat sheet: the sheet was closed to
  // reveal this push, so reverse that on back instead of popping to whatever
  // route happens to sit beneath it.
  const handleBack = useCallback(() => {
    navigation.goBack();
    setAiChatModalOpen(true);
  }, [navigation, setAiChatModalOpen]);

  const handleNutritionSave = useCallback(
    async ({ calories, proteinG, carbsG, fatsG }: NutritionValues) => {
      if (!mealData?.id) {
        showError("This meal is missing an ID and cannot be updated.");
        return;
      }

      try {
        const result = await editMealNutritionAsync({
          mealId: mealData.id,
          requestBody: {
            nutrition: {
              energy_kcal: calories,
              protein_grams: proteinG,
              carbs_grams: carbsG,
              fat_grams: fatsG,
            },
          },
          updateMealDetail: !fromPlan,
        });

        if (fromPlan) {
          showSuccess("Saved to Your Foods. Your current plan is unchanged.");
        } else {
          showSuccess(result.was_forked ? "Meal saved to Your Foods." : "Nutrition updated.");
        }
        setIsNutritionEditOpen(false);
      } catch (error) {
        showError(error instanceof Error ? error.message : "Unable to update nutrition.");
      }
    },
    [editMealNutritionAsync, fromPlan, mealData?.id, showError, showSuccess],
  );

  // Re-opens the chat sheet when leaving via the iOS swipe-back gesture, which
  // bypasses handleBack entirely. Idempotent with the handleBack call above.
  useIonViewWillLeave(() => {
    if (fromChatPicker) {
      setAiChatModalOpen(true);
    }
  });

  if (!mealData) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center">No meal data found</div>
      </PageContainer>
    );
  }

  if (!mealTime) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center">No meal time found</div>
      </PageContainer>
    );
  }
  const prepTime =
    mealData.totalTime !== null ||
    mealData.totalTime !== undefined ||
    mealData.totalTime !== "" ||
    mealData.totalTime !== "0"
      ? mealData.totalTime
      : null;
  const portionSize = mealData.portionSize;
  const imageHasWhiteBackground = mealData.imageHasWhiteBackground ?? false;
  const categoryPills = mealData.categoryPills ?? [];
  const notLikeable = mealData.isYourFood ?? false; // Your food is not likeable
  const editable = Boolean(mealData.id);
  console.log(mealData);
  console.assert(mealData.imageHasWhiteBackground !== null, "imageHasWhiteBackground is required");

  return (
    <IonPage ref={pageRef} className="bg-white">
      <SafeAreaContent fullscreen scrollY={false}>
        <div className="relative flex h-full w-full flex-col">
          <MealDetailHeader
            meal={mealData}
            likeable={!notLikeable}
            editable={editable}
            mealTime={mealTime}
            onBack={fromChatPicker ? handleBack : undefined}
            onEditClick={editable ? () => setIsNutritionEditOpen(true) : undefined}
          />

          <div
            ref={scrollContainerRef}
            className="no-scrollbar flex flex-1 flex-col overflow-y-auto"
          >
            <PlanOverviewImage
              imageUri={mealData.imageUri}
              name={mealData.name}
              notLikeable={notLikeable}
              imageHasWhiteBackground={imageHasWhiteBackground}
            />

            <div className="flex flex-col gap-6 px-4 pt-6 pb-24">
              <h1 className={cn(textVariants.mealTitle, "mx-4 text-center")}>{mealData.name}</h1>

              <MealDetailTags categoryPills={categoryPills} />

              <MealDetailMeta prepTime={prepTime} portionSize={portionSize} />

              <MealDescription description={mealData.description} />

              <MealAllergensTags allergens={mealData.allergens} />

              <div className="flex flex-col gap-4">
                {showCaloriesAndMacros && (
                  <MealDetailMacros nutritionalInfo={mealData.nutritionalInfo} />
                )}
              </div>

              <div ref={ingredientsSectionRef}>
                <MealIngredientsSection
                  meal={mealData}
                  mealDay={mealDay}
                  targetDate={targetDate}
                  fromPlan={fromPlan}
                />
              </div>

              <div className="flex flex-col gap-4">
                <MealFeedbackSection mealId={mealData.id} />
              </div>
            </div>
          </div>

          <MealDetailActions
            mealData={mealData}
            mealDay={mealDay}
            targetDate={targetDate}
            pageRef={pageRef}
            fromPlan={fromPlan}
            onIngredientsClick={scrollToIngredients}
          />
          {!fromChatPicker && !fromPlan && (
            <MealDetailAddMealFab
              mealData={mealData}
              mealTime={mealTime}
              notLikeable={notLikeable}
            />
          )}

          {editable && (
            <AmendMacrosModal
              isOpen={isNutritionEditOpen}
              onDismiss={() => setIsNutritionEditOpen(false)}
              onSave={handleNutritionSave}
              isSaving={isEditingMealNutrition}
              mealName={mealData.name ?? ""}
              calories={mealData.nutritionalInfo.perPortion?.energy?.kcal ?? 0}
              proteinG={mealData.nutritionalInfo.perPortion?.protein?.grams ?? 0}
              carbsG={mealData.nutritionalInfo.perPortion?.carbohydrate?.total?.grams ?? 0}
              fatsG={mealData.nutritionalInfo.perPortion?.fat?.total?.grams ?? 0}
            />
          )}
        </div>
      </SafeAreaContent>
    </IonPage>
  );
}
